// Mastery scoring + evidence windows. Phase C.
//
// Spec §"Mastery scoring and evidence windows" (lines 685–917).
// Operationalizes the spec's MasteryClassPolicy table as a series of
// gates rather than a single composite score. Accuracy informs
// mastery; it never replaces transfer, delayed review, independence,
// or content safety.
//
// In Phase C the transferEvidence + delayedReviewEvidence inputs come
// from Phase C's test fixtures (NOT_YET_GATED, etc.). Phase D replaces
// them with real builders without changing this module's signature.

import type {
  ChildAttempt,
  TransferEvidence,
  DelayedReviewEvidence,
  SkillMasteryState,
  MasteryClass,
  HintLevel,
  DecisionBlockerCode,
  DecisionReasonCode,
  RequiredNextEvidence,
} from "./types";
import {
  canCountTowardMastery,
  canCountTowardReadiness,
  isPostIdkSupported,
  revealPrimedCorrectAttempts,
} from "./supportModeGating";
import { inferMasteryClass, inferChannel } from "./masteryClassInference";

const HINT_ORDER: Record<HintLevel, number> = {
  H0: 0,
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
};

interface MasteryClassPolicy {
  minimumAttempts: number;
  recentWindowSize: number;
  accuracyThreshold: number;
  independenceThreshold: number;
  maxHintLevel: HintLevel;
  transferRequired: boolean;
  delayedReviewRequired: boolean;
  sameErrorBlocker: boolean;
  contentSafetyRequired: boolean;
}

export const MASTERY_POLICIES: Record<MasteryClass, MasteryClassPolicy> = {
  awareness: {
    minimumAttempts: 4,
    recentWindowSize: 6,
    accuracyThreshold: 0.90,
    independenceThreshold: 0.80,
    maxHintLevel: "H1",
    transferRequired: false,
    delayedReviewRequired: true,
    sameErrorBlocker: true,
    contentSafetyRequired: true,
  },
  directPractice: {
    minimumAttempts: 6,
    recentWindowSize: 8,
    accuracyThreshold: 0.85,
    independenceThreshold: 0.60,
    maxHintLevel: "H1",
    transferRequired: true,
    delayedReviewRequired: false,
    sameErrorBlocker: true,
    contentSafetyRequired: true,
  },
  strategyTransfer: {
    minimumAttempts: 8,
    recentWindowSize: 10,
    accuracyThreshold: 0.85,
    independenceThreshold: 0.70,
    maxHintLevel: "H1",
    transferRequired: true,
    delayedReviewRequired: false,
    sameErrorBlocker: true,
    contentSafetyRequired: true,
  },
  connectedText: {
    minimumAttempts: 6,
    recentWindowSize: 9,
    accuracyThreshold: 0.90,
    independenceThreshold: 0.75,
    maxHintLevel: "H1",
    transferRequired: true,
    delayedReviewRequired: false,
    sameErrorBlocker: true,
    contentSafetyRequired: true,
  },
  durableMastery: {
    minimumAttempts: 2, // delayed-review attempts
    recentWindowSize: 3,
    accuracyThreshold: 0.85,
    independenceThreshold: 0.70,
    maxHintLevel: "H1",
    transferRequired: true,
    delayedReviewRequired: true,
    sameErrorBlocker: true,
    contentSafetyRequired: true,
  },
};

/**
 * Compute a skill's mastery state from the windowed attempt evidence.
 *
 * Returns a SkillMasteryState that names provisionalMastery,
 * trueMastery, durableMastery, readinessOnly, blockers, reason codes,
 * and required-next-evidence — everything the decision engine needs
 * to route the next item.
 */
export function computeSkillMasteryState(
  skillId: string,
  attempts: ChildAttempt[],
  transferEvidence: TransferEvidence,
  delayedReviewEvidence: DelayedReviewEvidence,
): SkillMasteryState {
  const masteryClass = inferMasteryClass(skillId);
  const channel = inferChannel(skillId);
  const policy = MASTERY_POLICIES[masteryClass];

  const recentAttempts = attempts.slice(-policy.recentWindowSize);

  // ⭐MA-588 — computed over the FULL passed lookback, not the recent slice,
  // so a reveal that has just scrolled out of the recent window still primes
  // the re-answer inside it. EXCLUDE (not discount) from the counted-evidence
  // bar only: the real mastery gate is the minimumAttempts COUNT (see the
  // MA-135 block below — accuracyThreshold is provably dead), so a fractional
  // discount either still fills a count slot (free-ride survives) or doesn't
  // (which IS exclusion). The primed correct still occupies its positional
  // recent-window slot (exactly like the IDK row itself does today), still
  // counts toward readiness ("counts toward practice"), and deliberately does
  // NOT touch the independence numerator/denominator below — so no new
  // blockers, no selection-harshness change, and no MA-483-class defect
  // (removing attempts from that denominator can RAISE the rate).
  const revealPrimed = revealPrimedCorrectAttempts(attempts);

  const countedMasteryAttempts = recentAttempts.filter(
    (a) => canCountTowardMastery(a, channel) && !revealPrimed.has(a),
  );
  const countedReadinessAttempts = recentAttempts.filter((a) =>
    canCountTowardReadiness(a, channel),
  );

  // canCountTowardMastery already requires correctness, so
  // countedMasteryAttempts is correct-only by construction and this ratio
  // is always exactly 1.0 (or 0 when empty) — never a genuine percentage.
  // Kept for the returned SkillMasteryState.accuracyRate field (still read
  // by adaptationDecisionEngine's debug log + useSkillGraph's display
  // projection); see the MA-135 comment below `provisionalMastery` for why
  // this can no longer gate mastery on its own (ADR-038 Group B).
  const correctMasteryAttempts = countedMasteryAttempts.filter((a) => a.correct);
  const accuracyRate =
    countedMasteryAttempts.length === 0
      ? 0
      : correctMasteryAttempts.length / countedMasteryAttempts.length;

  // ⭐MA-483 — THE DENOMINATOR IS NOT `countedMasteryAttempts`, and that is the
  // entire point of this block.
  //
  // ⚠️THE DEFECT THIS AVOIDS (found by a plan-review agent, 2026-08-04, before
  // any of it shipped): `independentRate` used to be
  // `independentCount / countedMasteryAttempts.length`. Adding post-IDK to the
  // mastery exclusions REMOVES those attempts from that denominator — and
  // because four channels (oralVocabulary, listeningComprehension, handwriting,
  // numeralFormation) never consult `hintLevel`, a helped attempt used to land
  // in the denominator WITHOUT landing in the numerator. Excluding it therefore
  // RAISED the rate. Measured on the real manifest with ms.read.0a (policy
  // `awareness`, min 4 counted, independence >= 0.80): 4 independent + 2 post-IDK
  // went from counted=6 / rate=0.667 / provisionalMastery=FALSE to counted=4 /
  // rate=1.000 / provisionalMastery=TRUE — i.e. the "safety" change would have
  // certified a skill as Secure (masteryScore 0.7) that the code before it
  // refused to. A guard that grants the thing it exists to withhold.
  //
  // The fix: score independence over the attempts that WOULD have counted if the
  // child had not asked for help (numerator excludes them, denominator keeps
  // them), so a post-IDK attempt can only ever push this rate DOWN or leave it
  // flat. `countedMasteryAttempts` above still excludes them, so the mastery
  // COUNT is unaffected — the two sets are intentionally different.
  const independenceDenominator = recentAttempts.filter((a) =>
    canCountTowardMastery(
      isPostIdkSupported(a)
        ? { ...a, supportMode: a.supportMode.filter((m) => m !== "postIdkSupported") }
        : a,
      channel,
    ),
  );
  const independentCount = independenceDenominator.filter(
    (a) => !isPostIdkSupported(a) && HINT_ORDER[a.hintLevel] <= HINT_ORDER.H1,
  ).length;
  const independentRate =
    independenceDenominator.length === 0
      ? 0
      : independentCount / independenceDenominator.length;

  // Same-error loop: ≥2 of last 3 share a misconceptionId.
  const last3 = attempts.slice(-3);
  let sameErrorLoop = false;
  if (last3.length >= 3) {
    const lastMid = last3[last3.length - 1].misconceptionId;
    if (lastMid) {
      const count = last3.filter((a) => a.misconceptionId === lastMid).length;
      if (count >= 2) sameErrorLoop = true;
    }
  }

  const blockers: DecisionBlockerCode[] = [];
  const reasonCodes: DecisionReasonCode[] = [];
  const requiredNextEvidence: RequiredNextEvidence[] = [];

  // MA-135: this reads the same structurally-always-1.0-or-0 accuracyRate
  // documented above/below — left AS-IS deliberately (out of this cleanup's
  // scope; it only affects which parentExplanation.ts copy string is picked,
  // not any mastery/serve decision). Tracked as a separate, smaller issue.
  if (accuracyRate >= policy.accuracyThreshold) {
    reasonCodes.push("DIRECT_ACCURACY_STRONG");
  }

  if (sameErrorLoop) {
    blockers.push("BLOCK_SAME_ERROR_LOOP");
    reasonCodes.push("SAME_ERROR_LOOP_DETECTED");
    requiredNextEvidence.push("misconception_remediation");
  }

  const enoughAttempts = countedMasteryAttempts.length >= policy.minimumAttempts;
  const enoughIndependence = independentRate >= policy.independenceThreshold;

  if (!enoughAttempts) requiredNextEvidence.push("independent_retest");
  if (!enoughIndependence) {
    blockers.push("BLOCK_HIGH_HINT_USE");
    reasonCodes.push("HIGH_HINT_USAGE");
    requiredNextEvidence.push("independent_retest");
  }

  if (transferEvidence.required && !transferEvidence.passed) {
    blockers.push("BLOCK_NO_TRANSFER");
    reasonCodes.push("TRANSFER_REQUIRED");
    if (transferEvidence.requiresConnectedText) {
      requiredNextEvidence.push("connected_text_transfer");
    } else if (transferEvidence.requiresWordProblem) {
      requiredNextEvidence.push("word_problem_transfer");
    } else {
      requiredNextEvidence.push("transfer_task");
    }
  }

  if (delayedReviewEvidence.required && !delayedReviewEvidence.passed) {
    blockers.push("BLOCK_NO_DELAYED_REVIEW");
    reasonCodes.push("DELAYED_REVIEW_REQUIRED");
    requiredNextEvidence.push("delayed_review");
  }

  // MA-135 (hygiene): an `accuracyRate >= policy.accuracyThreshold` conjunct
  // used to sit here too. It was PROVABLY DEAD — countedMasteryAttempts is
  // already correct-only (see accuracyRate's definition above), so
  // accuracyRate is always exactly 1.0 once ANY evidence counts, and every
  // policy.minimumAttempts is >= 2 and every accuracyThreshold is < 1.0, so
  // `enoughAttempts` (below) already implies the conjunct for all 5 mastery
  // classes, unconditionally — removing it cannot change provisionalMastery
  // for any input, ever (see the equivalence bite in masteryEvidence.test.mjs
  // T-MA135). The REAL gate on this evidence is minimumAttempts-of-
  // recentWindowSize counted-correct attempts (e.g. 6-of-8 for
  // directPractice) — an evidence-COUNT bar, not an accuracy PERCENTAGE.
  // ADR-038 Group B flagged this accuracyThreshold family as "a third
  // independent quantity" without checking whether it actually binds; see
  // ADR-038's MA-135 addendum for the correction.
  const provisionalMastery = enoughAttempts && enoughIndependence && !sameErrorLoop;

  // MA-295 ("Mastered = confirmed on a later day", MA-DEC-0721): trueMastery
  // now carries the CROSS-DAY term — a passed delayed review (an
  // `isDelayedReview`-tagged, independent, correct attempt on a LATER
  // calendar day than prior practice; see spacedReview.evaluateDelayedReview's
  // cross-day strengthening). This is what the parent-facing "Mastered" tier
  // (projected score 0.9/1.0 ≥ MASTERY_SCORE_MASTERED) and the mascot key
  // off, so "Mastered" is never claimable on same-day evidence alone.
  // The transfer conjunct is unchanged in form but `required` is now the
  // serve+TAG capability gate (transferServingPathExists) instead of a
  // hardcoded `true` — see transferGating.ts MA-295 header.
  //
  // KNOWN DEGENERACY (deliberate, documented): with the delayed-review term
  // moved into trueMastery, `durableMastery`'s unchanged formula currently
  // coincides with trueMastery (its extra conjunct is the same evidence).
  // Parent-facing display is unaffected (both project ≥ 0.85 → the same
  // "mastered" tier; no raw score is ever shown). Differentiating durable
  // (e.g. a second, longer-gap review pass) is deferred to MA-303's slices
  // rather than invented here.
  const trueMastery =
    provisionalMastery &&
    delayedReviewEvidence.passed &&
    (!transferEvidence.required || transferEvidence.passed);

  const durableMastery =
    trueMastery && (!delayedReviewEvidence.required || delayedReviewEvidence.passed);

  // readinessOnly: some readiness evidence exists but mastery is blocked.
  const correctReadinessCount = countedReadinessAttempts.filter((a) => a.correct).length;
  const readinessOnly =
    !provisionalMastery &&
    correctReadinessCount >= policy.minimumAttempts &&
    !hasBlockingReadinessFailure(blockers);

  // MA-295 RED-review fix: TRANSFER_PASSED is an evidence claim — it must
  // reflect a real passed transfer, not trueMastery reached while transfer
  // was not required (parentExplanation maps this code to "transfer into a
  // new format" copy; emitting it without a transfer attempt would be one
  // consumer away from a false parent-facing claim).
  if (trueMastery && transferEvidence.passed) reasonCodes.push("TRANSFER_PASSED");
  if (durableMastery) reasonCodes.push("DELAYED_REVIEW_PASSED");

  return {
    skillId,
    domain: attempts[0]?.domain ?? "phonics_decoding",
    ageBand: attempts[0]?.ageBand ?? "5.0-5.5",
    channel,
    masteryClass,
    provisionalMastery,
    trueMastery,
    durableMastery,
    readinessOnly,
    accuracyRate,
    independentRate,
    highHintRate: 1 - independentRate,
    sameErrorLoop,
    connectedTextPassed: transferEvidence.requiresConnectedText
      ? transferEvidence.passed
      : false,
    wordProblemTransferPassed: transferEvidence.requiresWordProblem
      ? transferEvidence.passed
      : false,
    transferPassed: transferEvidence.passed,
    delayedReviewPassed: delayedReviewEvidence.passed,
    blockers,
    reasonCodes,
    requiredNextEvidence: Array.from(new Set(requiredNextEvidence)),
    evidenceSummary: {
      channel,
      masteryClass,
      attemptsConsidered: recentAttempts.length,
      countedAttempts: countedMasteryAttempts.length,
      readinessAttempts: countedReadinessAttempts.length,
      accuracyRate,
      independentRate,
      supportLeakageDetected: hasSupportLeakage(recentAttempts),
      transferPassed: transferEvidence.passed,
      delayedReviewPassed: delayedReviewEvidence.passed,
    },
  };
}

function hasBlockingReadinessFailure(blockers: DecisionBlockerCode[]): boolean {
  return blockers.includes("BLOCK_H4_SUCCESS") || blockers.includes("BLOCK_RAW_RUNTIME_LLM");
}

function hasSupportLeakage(attempts: ChildAttempt[]): boolean {
  return attempts.some(
    (a) =>
      a.readAloudUsed ||
      a.pictureSupportUsed ||
      a.adultSupportUsed ||
      a.mascotSupportUsed,
  );
}
